import { get, post, patch, del } from '../../helpers/api';
import client from './client';
import { gql } from '@apollo/client';

// The driver profile's own endpoints (hitch-services PR #3351). Separate from
// DriversApi on purpose: that one is the GraphQL funnel API behind the existing
// approvals page, and these are the REST document/standing surfaces. Drivers are
// addressed by Mongo `driver_id` here, same as the funnel routes.

// --- record ---------------------------------------------------------------

// Every document row per type, grouped by owner, with signed image URLs and
// derived readiness. Signed URLs expire in 15 minutes, so a stale record has
// unloadable images — re-read rather than holding this in a long-lived cache.
const getRecord = async (driverId) => {
  const { data } = await get(`/admin/drivers/${driverId}/documents`);
  return data;
};

// Per-check breakdown of what gates this driver: each required document and the
// background check. `derivedState` is what the inputs add up to, NOT necessarily
// their current standing — an admin decision isn't derivable.
const getStanding = async (driverId) => {
  const { data } = await get(`/admin/drivers/${driverId}/standing`);
  return data;
};

// --- writes ---------------------------------------------------------------

// Decide one document. Returns the whole refreshed record, so callers should
// replace their copy with the response instead of patching a row: readiness and
// the card roll-ups are derived server-side and will have moved.
//
// `expirationDate` omitted keeps the row's current expiry; passing null clears
// it. Those are different, so this only forwards the key when present.
const decideDocument = async (driverId, documentId, { status, reviewNote, internalNote, expirationDate }) => {
  const body = {};
  // Omitted status means "correct the fields, do not decide" — which is the only
  // way to fix a misread date on an EXPIRED row, since EXPIRED is derived and
  // cannot be sent back as a status.
  if (status !== undefined) body.status = status;
  if (reviewNote !== undefined) body.reviewNote = reviewNote;
  if (internalNote !== undefined) body.internalNote = internalNote;
  if (expirationDate !== undefined) body.expirationDate = expirationDate;
  const { data } = await patch(`/admin/drivers/${driverId}/documents/${documentId}`, body);
  return data;
};

// Record a document uploaded on the driver's behalf. Also returns the refreshed
// record. `driverVehicleId` is required for a vehicle-level type; the server
// asserts the vehicle actually belongs to this driver.
const addDocument = async (driverId, { documentType, imageUrl, driverVehicleId }) => {
  const body = { documentType, imageUrl };
  if (driverVehicleId) body.driverVehicleId = driverVehicleId;
  const { data } = await post(`/admin/drivers/${driverId}/documents`, body);
  return data;
};

// Add one eligible catalog vehicle to the driver. The response is the refreshed
// record, including the new vehicle's required document rows.
const addVehicle = async (driverId, vehicle) => {
  const { data } = await post(`/admin/drivers/${driverId}/vehicles`, vehicle);
  return data;
};

// Every screening report for the driver, newest first, each with the Checkr
// webhooks that moved it. Separate from the standing breakdown, which carries only
// the latest result because that is all the gate needs — screening is re-run several
// times a year, so the history is its own thing.
const getScreeningHistory = async (driverId) => {
  const { data } = await get(`/admin/drivers/${driverId}/screening`);
  return data;
};

// What the pipeline read off one document, per run, newest first. Fetched when a
// document is opened rather than with the record: the record would need one query
// per row to carry it, and only the open one is ever looked at.
const getExtraction = async (driverId, documentId) => {
  const { data } = await get(`/admin/drivers/${driverId}/documents/${documentId}/extraction`);
  return data;
};

// Put a document back through the verification pipeline by hand. `stage` is
// 'extraction' (re-read the image — three model calls) or 'decision' (re-decide
// from the stored extraction, free).
//
// 202, not 200: the pipeline is asynchronous, so this only acknowledges that the
// message was queued. Nothing is decided by the time it returns — the caller has
// to re-read to see an outcome, which is why it does NOT return a record.
const replayDocument = async (driverId, documentId, stage) => {
  const { data } = await post(`/admin/drivers/${driverId}/documents/${documentId}/replay`, { stage });
  return data;
};

// Correct a vehicle's VIN, plate or colour, or grandfather this driver onto a
// retired model. Returns the refreshed record, same as the document writes. Make,
// model and year are absent by design: they live on the shared catalog row, so they
// are not per-driver corrections — and `isGrandfathered` is the per-driver exception
// to the eligibility flag on that same shared row.
//
// Only sends keys that are present. Absent means "leave alone" and null means
// "clear" — collapsing the two would wipe a plate on every partial edit.
const updateVehicle = async (driverId, driverVehicleId, fields) => {
  const body = {};
  if (fields.vin !== undefined) body.vin = fields.vin;
  if (fields.licensePlate !== undefined) body.licensePlate = fields.licensePlate;
  if (fields.color !== undefined) body.color = fields.color;
  if (fields.isGrandfathered !== undefined) body.isGrandfathered = fields.isGrandfathered;
  const { data } = await patch(`/admin/drivers/${driverId}/vehicles/${driverVehicleId}`, body);
  return data;
};

// Delete a vehicle. Its documents go with it, and if it was the default another
// vehicle takes over — the same path the driver's own app deletes through.
// Returns the refreshed record, like the other writes here.
const deleteVehicle = async (driverId, driverVehicleId) => {
  const { data } = await del(`/admin/drivers/${driverId}/vehicles/${driverVehicleId}`);
  return data;
};

// Set standing directly without changing application status.
const actOnStanding = async (driverId, standing, reason) => {
  const body = { standing };
  if (reason) body.reason = reason;
  const { data } = await patch(`/admin/drivers/${driverId}/standing`, body);
  return data;
};

// Recompute standing from the driver's current documents and screening, and store the
// result. Synchronous, and returns the same breakdown `getStanding` does, so the caller
// replaces their copy with the response.
//
// Two things the call site cannot see. It WRITES — a standing event attributed to the
// calling admin, because no document decision or webhook explains this change. And it
// deliberately writes NOTHING for a standing the evaluation does not own (suspended,
// deactivated): those are human decisions, and re-derivation must not undo one. The
// unchanged breakdown comes back either way, so the response is the only honest
// account of what happened.
const reevaluateStanding = async (driverId) => {
  const { data } = await post(`/admin/drivers/${driverId}/standing/reevaluate`);
  return data;
};

// --- lists ---------------------------------------------------------------

// Documents waiting on a person, oldest first. Defaults to PENDING +
// NEEDS_REVIEW server-side.
const getReviewQueue = async ({ status, level, olderThanHours, limit = 25, offset = 0 } = {}) => {
  const params = new URLSearchParams({ limit, offset });
  // Repeated key, not a comma list — the DTO validates an array.
  (status || []).forEach((s) => params.append('status', s));
  if (level) params.set('level', level);
  if (olderThanHours) params.set('olderThanHours', olderThanHours);
  const { data } = await get(`/admin/drivers/documents/queue?${params.toString()}`);
  return data;
};

// Drivers in a given standing, or every approved driver when standing is omitted.
// The only way to list suspended or deactivated drivers — standing exists
// post-approval, so this never returns applicants.
const getDriversByStanding = async ({ standing, limit = 25, offset = 0 } = {}) => {
  const params = new URLSearchParams({ limit, offset });
  if (standing) params.set('standing', standing);
  const { data } = await get(`/admin/drivers/by-standing?${params.toString()}`);
  return data;
};

// The pre-approval complement: drivers who have never been approved, filtered by
// the collapsed status their own app shows rather than the stored one. Between
// this and by-standing every driver is listed exactly once.
const getApplicants = async ({ status, limit = 25, offset = 0 } = {}) => {
  const params = new URLSearchParams({ limit, offset });
  if (status) params.set('status', status);
  const { data } = await get(`/admin/drivers/applicants?${params.toString()}`);
  return data;
};

// How many drivers sit behind each filter, both phases at once. One request rather
// than one per bucket — the list header shows all of them before anything is
// clicked. `byState` is keyed by the same values the list endpoints take.
const getCounts = async () => {
  const { data } = await get('/admin/drivers/counts');
  return data;
};

// --- upload --------------------------------------------------------------

// Presigned POST policy for the driver document bucket. Already existed for the
// old approvals screen; the profile reuses it and then calls addDocument, which
// is the step the old path was missing.
const getUploadPolicy = async (driverId) => {
  const data = await client.query(
    gql`
      query presignedUrl($id: String!) {
        presignedUrl(id: $id) {
          url
          fileName
          fileUrl
        }
      }
    `,
    { id: driverId },
  );
  return data.presignedUrl;
};

const driverProfileApi = {
  getRecord,
  getStanding,
  decideDocument,
  addDocument,
  addVehicle,
  getScreeningHistory,
  getExtraction,
  replayDocument,
  updateVehicle,
  deleteVehicle,
  actOnStanding,
  reevaluateStanding,
  getReviewQueue,
  getDriversByStanding,
  getApplicants,
  getCounts,
  getUploadPolicy,
};

export default driverProfileApi;
